import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/use-async';
import { ConsentOption } from '../lib/types';
import { actionLabel } from '../lib/format';
import { Badge, Card, Empty, Notice, PageHead } from '../components/ui';

interface OptionsResponse {
  application: { id: string; name: string; redirectUris: string[] };
  module: { id: string; key: string; name: string };
  options: ConsentOption[];
}

/**
 * The consent page. This is where an application sends its users once the
 * gateway has reported CONSENT_REQUIRED.
 */
export function ConsentGrant() {
  const [params] = useSearchParams();
  const applicationId = params.get('application') ?? '';
  const moduleKey = params.get('module') ?? '';
  const redirectUri = params.get('redirect_uri') ?? undefined;

  const data = useAsync(
    () =>
      api.get<OptionsResponse>(
        `/api/me/consent-options?applicationId=${encodeURIComponent(applicationId)}&moduleKey=${encodeURIComponent(moduleKey)}`,
      ),
    [applicationId, moduleKey],
  );

  const [selection, setSelection] = useState<Set<string> | null>(null);
  const [saved, setSaved] = useState<{ granted: number; rejected: number; redirectUri: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const grantable = useMemo(
    () => (data.data?.options ?? []).filter((option) => option.availableToUser && option.requestedByApplication),
    [data.data],
  );

  const selected =
    selection ?? new Set(grantable.filter((option) => option.granted).map((option) => key(option)));

  function toggle(option: ConsentOption): void {
    const next = new Set(selected);
    if (next.has(key(option))) next.delete(key(option));
    else next.add(key(option));
    setSelection(next);
  }

  async function submit(): Promise<void> {
    setError(null);
    try {
      const result = await api.post<{ granted: unknown[]; rejected: unknown[]; redirectUri: string | null }>(
        '/api/me/consents',
        {
          applicationId,
          moduleKey,
          selections: [...selected].map((entry) => {
            const [resource, action] = entry.split(' ');
            return { resource, action };
          }),
          redirectUri,
        },
      );
      setSaved({
        granted: result.granted.length,
        rejected: result.rejected.length,
        redirectUri: result.redirectUri,
      });
      if (result.redirectUri) window.location.href = result.redirectUri;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  if (data.error) return <Notice tone="danger">{data.error}</Notice>;
  if (!data.data) return <p className="muted">Loading...</p>;

  const notAvailable = (data.data.options ?? []).filter(
    (option) => option.requestedByApplication && !option.availableToUser,
  );

  return (
    <>
      <PageHead
        title={`Allow ${data.data.application.name} access`}
        description={`The application would like to access ${data.data.module.name} in your name. You decide what part of it.`}
      />

      {error ? <Notice tone="danger">{error}</Notice> : null}
      {saved ? (
        <Notice tone="ok" title="Saved">
          {saved.granted} permissions granted{saved.rejected ? `, ${saved.rejected} not possible` : ''}.
          {saved.redirectUri ? ' You are being redirected.' : ''}
        </Notice>
      ) : null}

      <Card title="What you can allow" tight>
        {grantable.length ? (
          <>
            <div className="matrix-scroll">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 40 }} />
                    <th>Data</th>
                    <th>Action</th>
                    <th>Kind</th>
                  </tr>
                </thead>
                <tbody>
                  {grantable.map((option) => (
                    <tr key={key(option)}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(key(option))}
                          onChange={() => toggle(option)}
                        />
                      </td>
                      <td>{option.resource === '*' ? 'all data' : option.resource}</td>
                      <td>{actionLabel(option.action)}</td>
                      <td>
                        {option.isWrite ? <Badge tone="warn">modifying</Badge> : <Badge>read only</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="row end" style={{ padding: 12 }}>
              <span className="muted" style={{ marginRight: 'auto' }}>
                {selected.size} of {grantable.length} selected
              </span>
              <button onClick={() => setSelection(new Set(grantable.filter((o) => !o.isWrite).map(key)))}>
                Read only
              </button>
              <button onClick={() => setSelection(new Set(grantable.map(key)))}>Everything</button>
              <button className="primary" onClick={() => void submit()}>
                Grant consent
              </button>
            </div>
          </>
        ) : (
          <Empty>
            There is nothing you could grant to this application - either it requests nothing, or you lack the
            permissions yourself.
          </Empty>
        )}
      </Card>

      {notAvailable.length ? (
        <Card title="Requested by the application, but not possible for you" tight>
          <div className="body">
            <p className="muted">
              You can only pass on what you are entitled to yourself. You lack the permissions for the following:
            </p>
            <div className="row">
              {notAvailable.slice(0, 40).map((option) => (
                <Badge key={key(option)}>
                  {option.resource} {actionLabel(option.action)}
                </Badge>
              ))}
              {notAvailable.length > 40 ? <span className="muted">and {notAvailable.length - 40} more</span> : null}
            </div>
          </div>
        </Card>
      ) : null}
    </>
  );
}

function key(option: ConsentOption): string {
  return `${option.resource} ${option.action}`;
}
